<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiLink, getUrgencyLabel } from '@shopexperts/shared';
import type { LeadExpertResponse } from '@shopexperts/shared';

interface ExpertLeadCardProps {
    lead: LeadExpertResponse;
}

const props = defineProps<ExpertLeadCardProps>();

// Format date
const formattedCreatedDate = computed(() => {
    const date = new Date(props.lead.created_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

const formattedMatchedDate = computed(() => {
    if (!props.lead.matched_at) return null;
    const date = new Date(props.lead.matched_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

// Match type badge styling
const matchTypeBadge = computed(() => {
    if (props.lead.match.match_type === 'client_selected') {
        return {
            label: 'Client Selected',
            class: 'bg-blue-100 text-blue-800'
        };
    }
    return {
        label: 'Admin Matched',
        class: 'bg-orange-100 text-orange-800'
    };
});

// Urgency badge color
const urgencyColor = computed(() => {
    switch (props.lead.urgency) {
        case 'urgent':
        case 'as_soon_as_possible':
            return 'bg-red-100 text-red-800';
        case 'within_the_next_week':
            return 'bg-orange-100 text-orange-800';
        case 'within_the_next_month':
            return 'bg-yellow-100 text-yellow-800';
        case 'standard':
        case 'open_to_expert_opinion':
        default:
            return 'bg-blue-100 text-blue-800';
    }
});

// Urgency display label
const urgencyLabel = computed(() => getUrgencyLabel(props.lead.urgency));

// Client display name
const clientName = computed(() => {
    return props.lead.client.owner_name || props.lead.client.name;
});
</script>

<template>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch">
            <!-- Left Column: Project Details -->
            <div class="flex-1 flex flex-col min-w-0">
                <!-- Project Header -->
                <div class="mb-3">
                    <div class="flex items-center gap-2 flex-wrap mb-1">
                        <UiText as="span" variant="p-small" class="text-gray-400">#{{ lead.id }}</UiText>
                        <span 
                            :class="matchTypeBadge.class" 
                            class="px-2 py-0.5 rounded text-xs font-medium"
                        >
                            {{ matchTypeBadge.label }}
                        </span>
                        <span :class="urgencyColor" class="px-2 py-0.5 rounded text-xs font-medium">
                            {{ urgencyLabel }}
                        </span>
                    </div>
                    <UiText as="h4" variant="h5" class="font-semibold">
                        {{ lead.project_title }}
                    </UiText>
                    <UiLink 
                        :href="lead.store.url" 
                        external="with-arrow" 
                        variant="p-small"
                        :label="lead.store.title" 
                    />
                </div>

                <!-- Project Brief -->
                <UiText 
                    v-if="lead.project_brief" 
                    as="p" 
                    variant="p-small" 
                    class="text-gray-600 mb-3"
                    :text="lead.project_brief" 
                    :truncate="3" 
                    :show-more-button="true" 
                />

                <!-- Spacer -->
                <div class="flex-1"></div>

                <!-- Client Info -->
                <div class="flex items-center gap-3 mb-3 p-2 bg-gray-50 rounded-md">
                    <div class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                        <span class="text-gray-500 text-sm font-medium">
                            {{ clientName.charAt(0).toUpperCase() }}
                        </span>
                    </div>
                    <div class="flex-1 min-w-0">
                        <UiText as="p" variant="p-small" class="font-semibold truncate">
                            {{ clientName }}
                        </UiText>
                        <UiLink 
                            v-if="lead.client.owner_email"
                            :href="`mailto:${lead.client.owner_email}`" 
                            variant="caption"
                            :label="lead.client.owner_email" 
                            class="text-blue-600 hover:underline truncate block"
                        />
                    </div>
                </div>

                <!-- Metadata Row -->
                <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    <div>
                        <UiText as="span" variant="caption-normal" class="text-gray-500">Budget: </UiText>
                        <UiText as="span" variant="caption-normal" class="font-semibold">{{ lead.budget_range }}</UiText>
                    </div>
                    <div>
                        <UiText as="span" variant="caption-normal" class="text-gray-500">Created: </UiText>
                        <UiText as="span" variant="caption-normal" class="font-semibold">{{ formattedCreatedDate }}</UiText>
                    </div>
                    <div v-if="formattedMatchedDate">
                        <UiText as="span" variant="caption-normal" class="text-gray-500">Matched: </UiText>
                        <UiText as="span" variant="caption-normal" class="font-semibold">{{ formattedMatchedDate }}</UiText>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>
